import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { FormField, Input, Select, Textarea } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import { useAbility } from '../../context/AbilityContext.js';
import { api } from '../../services/api.js';
import {
  PENALTY_MAX_MINOR,
  PENALTY_TYPES,
  PENALTY_TYPE_LABEL,
  type Penalty,
  type PenaltyType,
  mytToday,
  penaltyPowers,
} from './penalties.js';

/**
 * 371 (#1241): add or edit a penalty. A new one is recorded against a booking (chosen here, or the booking page's
 * own); the customer, car and supplier come from that booking on the server. Editing changes the type, amount,
 * day and reason only.
 */
export interface BookingChoice {
  id: string;
  /** e.g. `aa000001 · Aina Rahman · VKA 1234`. */
  label: string;
}
interface FoundBooking {
  id: string;
  car: { plate_number: string } | null;
  customer?: { name: string } | null;
}

export const bookingLabel = (b: FoundBooking) =>
  [b.id.slice(-8), b.customer?.name, b.car?.plate_number].filter(Boolean).join(' · ');

function BookingPicker({ onPick }: { onPick: (b: BookingChoice) => void }) {
  const [q, setQ] = useState('');
  const term = q.trim();
  const found = useQuery({
    queryKey: ['penalty-booking-search', term],
    queryFn: () =>
      api.get(`/bookings?${new URLSearchParams({ q: term, limit: '8', page: '1' })}`) as Promise<{
        items: FoundBooking[];
      }>,
    enabled: term.length >= 2,
  });
  return (
    <div className="flex flex-col gap-2">
      <Input
        id="penalty-booking"
        value={q}
        placeholder="Booking reference, customer name or phone"
        onChange={(e) => setQ(e.target.value)}
      />
      {term.length >= 2 && found.data && (
        <ul aria-label="Matching bookings" className="flex flex-col gap-1">
          {found.data.items.length === 0 && (
            <li className="text-sm text-steel">No booking found.</li>
          )}
          {found.data.items.map((b) => (
            <li key={b.id}>
              <button
                type="button"
                className="w-full rounded px-2 py-1 text-left text-sm text-ink hover:bg-surface"
                onClick={() => onPick({ id: b.id, label: bookingLabel(b) })}
              >
                {bookingLabel(b)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const toMinor = (rm: string) => Math.round(Number.parseFloat(rm) * 100);

export function PenaltyDialog({
  open,
  onClose,
  penalty,
  booking,
}: {
  open: boolean;
  onClose: () => void;
  /** Editing this penalty. */
  penalty?: Penalty;
  /** A new penalty on this booking (the booking page). */
  booking?: BookingChoice;
}) {
  const qc = useQueryClient();
  // 412: recording a penalty as already paid confirms a payment.
  const mayConfirm = penaltyPowers(useAbility()).confirm;
  const [picked, setPicked] = useState<BookingChoice | null>(booking ?? null);
  const [type, setType] = useState<PenaltyType>(penalty?.type ?? 'late_return');
  const [amount, setAmount] = useState(penalty ? (penalty.amount_minor / 100).toFixed(2) : '');
  const [on, setOn] = useState(penalty?.on ?? mytToday());
  const [paid, setPaid] = useState(false);
  const [reason, setReason] = useState(penalty?.reason ?? '');
  const [problem, setProblem] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      penalty ? api.patch(`/penalties/${penalty.id}`, body) : api.post('/penalties', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['penalties'] });
      onClose();
    },
    onError: (e) => setProblem((e as { message?: string }).message ?? 'Save failed.'),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const minor = toMinor(amount);
    if (!penalty && !picked) return setProblem('Choose the booking.');
    if (!Number.isFinite(minor) || minor < 1 || minor > PENALTY_MAX_MINOR)
      return setProblem('Enter an amount between RM 0.01 and RM 10,000.');
    if (!reason.trim()) return setProblem('Give the reason.');
    setProblem(null);
    const body: Record<string, unknown> = { type, amount_minor: minor, on, reason: reason.trim() };
    if (!penalty) {
      body.booking_id = picked?.id;
      if (paid && mayConfirm) body.customer_paid_on = on;
    }
    save.mutate(body);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={penalty ? 'Edit penalty' : 'Add penalty'}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="penalty-form" disabled={save.isPending}>
            {penalty ? 'Save penalty' : 'Add penalty'}
          </Button>
        </div>
      }
    >
      <form id="penalty-form" onSubmit={submit} className="flex flex-col gap-3">
        <FormField label="Booking" name="penalty-booking">
          {penalty ? (
            <p className="text-sm text-ink">
              {[penalty.booking_ref, penalty.customer.name, penalty.car.plate_number]
                .filter(Boolean)
                .join(' · ')}
            </p>
          ) : picked ? (
            <div className="flex items-center gap-3 text-sm text-ink">
              <span data-testid="penalty-picked">{picked.label}</span>
              {!booking && (
                <button
                  type="button"
                  className="text-xs text-primary underline"
                  onClick={() => setPicked(null)}
                >
                  Change
                </button>
              )}
            </div>
          ) : (
            <BookingPicker onPick={setPicked} />
          )}
        </FormField>
        <div className="grid gap-3 sm:grid-cols-3">
          <FormField label="Type" name="penalty-type">
            <Select
              id="penalty-type"
              value={type}
              onChange={(e) => setType(e.target.value as PenaltyType)}
            >
              {/* 417: Extra kilometres comes only from a booking's km charge; an existing one keeps its type. */}
              {PENALTY_TYPES.filter((t) => t !== 'mileage' || type === 'mileage').map((t) => (
                <option key={t} value={t}>
                  {PENALTY_TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Amount (RM)" name="penalty-amount">
            <Input
              id="penalty-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </FormField>
          <FormField label="Day" name="penalty-on">
            <Input
              id="penalty-on"
              type="date"
              max={mytToday()}
              value={on}
              onChange={(e) => setOn(e.target.value)}
            />
          </FormField>
        </div>
        {!penalty && mayConfirm && (
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} />
            Already paid by the customer
          </label>
        )}
        <FormField label="Reason" name="penalty-reason">
          <Textarea
            id="penalty-reason"
            rows={3}
            maxLength={300}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>
        <p className="text-xs text-steel">
          {penalty
            ? `Supplier ${penalty.supplier.name || '—'} (from the booking).`
            : 'The customer, car and supplier come from the booking.'}
        </p>
        {problem && (
          <p role="alert" className="text-sm text-semantic-danger-text">
            {problem}
          </p>
        )}
      </form>
    </Modal>
  );
}
